import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import PoemCard from "@/components/public/PoemCard";
import { Button } from "@/components/ui/button";

export default function Poems() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [poems, setPoems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);
  const activeTag = searchParams.get("tag") || "";

  useEffect(() => {
    api.get("/poem-categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/poem-tags").then(({ data }) => setTags(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (active !== "all") params.category = active;
    if (activeTag) params.tag = activeTag;
    api.get("/poems", { params }).then(({ data }) => setPoems(data)).catch(() => setPoems([])).finally(() => setLoading(false));
  }, [active, activeTag]);

  const toggleTag = (t) => {
    const next = new URLSearchParams(searchParams);
    if (activeTag === t) next.delete("tag"); else next.set("tag", t);
    setSearchParams(next);
  };

  return (
    <div className="on-dark violet-canvas fade-in flex-1 w-full">
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-12 sm:py-16">
        <SEO title="Gedichte" description="Gedichte und Gedanken von Susanne Monika Gysin." />
        <header className="mb-10 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F4C542] mb-4">Gedichte & Gedanken</div>
          <h1 className="font-serif text-5xl sm:text-6xl font-light text-[#FFF9F2]">Worte</h1>
        </header>

        <div className="flex flex-wrap justify-center gap-2 mb-6">
          <Chip active={active === "all"} onClick={() => setActive("all")} testId="poem-cat-all">Alle</Chip>
          {categories.map((c) => (
            <Chip key={c.id} active={active === c.name} onClick={() => setActive(c.name)} testId={`poem-cat-${c.slug}`}>{c.name}</Chip>
          ))}
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="poems-tag-filter">
            {tags.map((t) => (
              <button
                key={t}
                onClick={() => toggleTag(t)}
                data-testid={`poems-tag-${t}`}
                className={`rounded-full h-8 px-3.5 text-sm transition-colors ${activeTag === t ? "bg-[#F4C542] text-[#32104F]" : "text-[#D8B9EA] border border-[#632A8E] hover:border-[#F4C542]"}`}
              >
                #{t}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-center text-[#D8B9EA]">Laden…</p>
        ) : poems.length ? (
          <div>
            {poems.map((p) => <PoemCard key={p.id} poem={p} />)}
          </div>
        ) : (
          <div className="py-24 text-center">
            <p className="font-serif text-2xl text-[#D8B9EA]">Noch keine Gedichte</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full h-9 px-4 text-sm ${active ? "bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] hover:text-white" : "text-[#D8B9EA] hover:bg-[#3C1A5C] hover:text-[#FFF9F2]"}`}
    >
      {children}
    </Button>
  );
}
